<form
  [formGroup]="rulesForm"
  class="flex flex-col gap-3 max-w-[750px] @container"
  *ngIf="property && (initialized$ | async)">
  <div class="flex flex-col gap-3 h-full flex-1">
    <!-- Min stay -->
    <div class="mb-4 flex flex-col">
      <div class="flex flex-col @md:flex-row justify-between gap-2">
        <div class="flex flex-col gap-1">
          <strong class="strong text-sm">Min stay</strong>
          <div class="text__small">Shortest stay guests can book.</div>
        </div>
        <mat-form-field class="@md:w-36 @md:max-w-36 nopadding nomargin">
          <input
            matInput
            formControlName="min_stay"
            type="number"
            min="0"
            max="1125"
            class="!text-right"
            [errorStateMatcher]="immediateErrorMatcher" />
          <span matSuffix>{{ 'nights' | pluralize: rulesForm.get('min_stay').value }}</span>
          <mat-error *ngIf="!rulesForm.get('min_stay').errors?.lteProp">Must be 1125 nights or less</mat-error>
          <mat-error *ngIf="rulesForm.get('min_stay').errors?.lteProp">Cannot be more than max stay</mat-error>
        </mat-form-field>
      </div>

      <div *ngIf="rulesForm.get('min_stay')?.value && property.counts.airbnb" class="flex flex-col gap-3">
        <div>
          <mat-checkbox [formControl]="customizeMinStayCtrl" class="nopadding">
            <span class="strong text-sm">Customize by check-in day</span>
          </mat-checkbox>
          <div class="text__small ml-7">Set minimum nights by check-in day (Airbnb only).</div>
        </div>
        <div *ngIf="customizeMinStayCtrl.value" class="flex flex-col @md:flex-row flex-wrap gap-2">
          <div *ngFor="let dayCtrl of minStayDaysPreferred; let i = index" class="flex flex-col gap-1">
            <label class="text-sm strong">{{ minStayDaysLabels[i] }}</label>
            <mat-form-field class="@md:max-w-[100px] @md:w-[100px] nopadding input__mini">
              <input matInput [formControl]="dayCtrl" type="number" min="0" max="1125" class="!text-right" />
              <span matSuffix>{{ 'nights' | pluralize: dayCtrl.value }}</span>
              <mat-error *ngIf="!dayCtrl.errors?.lteProp">Must be 1125 nights or less</mat-error>
              <mat-error *ngIf="dayCtrl.errors?.lteProp">Cannot be more than max stay</mat-error>
            </mat-form-field>
          </div>
        </div>
      </div>
    </div>

    <!-- Max stay -->
    <div class="mb-4 flex flex-col gap-3">
      <div class="flex flex-col @md:flex-row justify-between gap-2">
        <div class="flex flex-col gap-1">
          <strong class="strong text-sm">Max stay</strong>
          <div class="text__small">Longest stay guests can book.</div>
        </div>
        <mat-form-field class="@md:w-36 @md:max-w-36 nopadding nomargin">
          <input
            matInput
            formControlName="max_stay"
            type="number"
            min="0"
            max="1125"
            class="!text-right"
            [errorStateMatcher]="immediateErrorMatcher" />
          <span matSuffix>{{ 'nights' | pluralize: rulesForm.get('max_stay').value }}</span>
          <mat-error *ngIf="!rulesForm.get('max_stay').errors?.gteProp">Must be 1125 nights or less</mat-error>
          <mat-error *ngIf="rulesForm.get('max_stay').errors?.gteProp">Cannot be less than minimum stay</mat-error>
        </mat-form-field>
      </div>

      <div
        *ngIf="rulesForm.get('max_stay')?.value && rulesForm.get('max_stay').value >= 28 && property.counts.airbnb"
        class="flex flex-col gap-2">
        <mat-checkbox formControlName="allow_rtb_above_max_nights" class="nopadding">
          <strong class="text-sm">Allow requests for longer stays</strong>
        </mat-checkbox>
        <div class="text__small ml-7">
          If your listing has enabled Instant Book, let guests request to book for longer than your maximum stay (Airbnb
          and Direct only).
        </div>
      </div>
    </div>

    <!-- Restricted Checkin -->
    <div class="flex flex-col @md:flex-row justify-between gap-3 mb-4">
      <div class="flex flex-col gap-2 max-w-[400px] flex-1">
        <strong class="text-sm">Restricted Check-in</strong>
        <div class="text__small">Guests can't start stays on these days.</div>
      </div>

      <div class="flex flex-col @md:items-end flex-1">
        <sbnb-day-toggle-group
          [startWeekOnSunday]="startWeekOnSunday"
          formControlName="restricted_check_in"></sbnb-day-toggle-group>

        <mat-error *ngIf="rulesForm.getError('arrayMax', 'restricted_check_in')">
          <div class="text__small">You have to allow check-in on at least one day</div>
        </mat-error>
      </div>
    </div>

    <!-- Restricted Checkout -->
    <div class="flex flex-col @md:flex-row justify-between gap-3 mb-4">
      <div class="flex flex-col gap-2 max-w-[400px] flex-1">
        <strong class="text-sm">Restricted Checkout</strong>
        <div class="text__small">Guests can't end stays on these days.</div>
      </div>
      <div class="flex flex-col @md:items-end flex-1">
        <sbnb-day-toggle-group
          [startWeekOnSunday]="startWeekOnSunday"
          formControlName="restricted_check_out"></sbnb-day-toggle-group>

        <mat-error *ngIf="rulesForm.getError('arrayMax', 'restricted_check_out')">
          <div class="text__small">You have to allow checkout on at least one day</div>
        </mat-error>
      </div>
    </div>

    <!-- Preparation Time -->
    <div class="flex flex-wrap flex-col @md:flex-row justify-between gap-3">
      <div class="flex flex-col gap-2 max-w-[400px]">
        <strong class="text-sm">Preparation Time</strong>
        <div class="text__small">Turnover nights blocked after each stay.</div>
      </div>
      <mat-form-field>
        <mat-select formControlName="preparation_time">
          <mat-option *ngFor="let option of preparationTimeOptions" [value]="option.value">
            {{ option.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>

    <!-- Advance Notice -->
    <div class="mb-4 flex flex-col gap-3">
      <div class="flex flex-wrap flex-col @md:flex-row justify-between gap-3">
        <div class="flex flex-col gap-2">
          <strong class="text-sm">Advance Notice</strong>
          <div class="text__small">Minimum time between booking and check-in.</div>
        </div>
        <div class="flex gap-3 flex-1 items-center justify-end">
          <mat-form-field class="w-full @md:w-40 nopadding">
            <mat-select formControlName="advance_notice">
              <mat-option *ngFor="let option of advanceNoticeOptions" [value]="option.value">
                {{ option.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field *ngIf="rulesForm.get('advance_notice')?.value === 1" class="w-40 nopadding">
            <mat-select formControlName="advance_notice_same_day">
              <mat-option *ngFor="let option of advanceNoticeSameDayOptions" [value]="option.value">
                {{ option.label }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>

      <div *ngIf="rulesForm.get('advance_notice')?.value && property.counts.airbnb" class="flex flex-col gap-1">
        <mat-checkbox formControlName="advance_notice_allow_request_to_book" class="nopadding">
          <strong class="text-sm">Allow shorter notice requests</strong>
        </mat-checkbox>

        <div class="text__small ml-7">
          If your listing has enabled Instant Book, let guests request with less notice than your advance notice setting
          (Airbnb & Direct only).
        </div>
      </div>
    </div>

    <!-- Availability Window -->
    <div class="flex flex-wrap flex-col @md:flex-row justify-between gap-3">
      <div class="flex flex-col gap-2">
        <strong class="text-sm">Availability Window</strong>
        <div class="text__small">How far in advance you want guests to be able to book.</div>
      </div>
      <mat-form-field class="w-full @md:w-40">
        <mat-select formControlName="availability_window">
          <mat-option *ngFor="let option of availabilityWindowOptions" [value]="option.value">
            {{ option.label }}
          </mat-option>
        </mat-select>
      </mat-form-field>
    </div>
  </div>

  <div class="flex justify-between flex-wrap gap-3 border-smoke border-t sticky-footer bottom-0 bg-white py-4">
    <sbnb-button type="secondary" (clicked)="reset()" [disabled]="rulesForm.pristine || (saving$ | async)">
      Cancel
    </sbnb-button>
    <sbnb-button
      [loading]="saving$ | async"
      loadingLabel="Saving..."
      label="Save"
      (clicked)="save()"
      [disabled]="rulesForm.invalid || rulesForm.pristine || (saving$ | async)"></sbnb-button>
  </div>
</form>
